'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { CvssMetrics, Cvss4Metrics, CvssVersion } from './types';
import type { CvssMetricKey, Cvss4MetricKey } from './types';
import {
  calculateCvssScore,
  calculateEnvironmentalScore,
  generateVectorString,
  isEnvironmentalRequirementsConfigured,
  parseVectorString,
  areAllMetricsSelected,
  getInitialMetrics,
  getSeverityFromScore,
  getCvssVersionFromVector,
  resolveEnvironmentalRequirementsForFindingVersion,
  stripEnvironmentalMetricsFromVector,
  type CvssEnvironmentalRequirements,
} from '@terra/shared-cvss';

type UseCvssCalculatorOptions = {
  initialScore?: string;
  initialVector?: string;
  initialEnvironmentalVector?: string | null;
  version?: CvssVersion;
  environmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
  onScoreChange: (score: string, vector: string, severity?: string) => void;
};

type EnvironmentalDisplay = {
  score: string;
  vector: string;
};

type EmittedCvssScore = {
  score: string;
  vector: string;
  severity: string;
};

const toBaseMetricVector = (vector: string): string =>
  stripEnvironmentalMetricsFromVector(vector);

const buildMetricsFromVector = (vector: string): CvssMetrics | Cvss4Metrics => {
  const baseVector = toBaseMetricVector(vector);
  const vectorVersion = getCvssVersionFromVector(baseVector);
  const base =
    vectorVersion === '4.0' ? getInitialMetrics('4.0') : getInitialMetrics();

  return { ...base, ...parseVectorString(baseVector) };
};

const getEmptyMetrics = (version: CvssVersion): CvssMetrics | Cvss4Metrics =>
  version === '4.0' ? getInitialMetrics('4.0') : getInitialMetrics();

export const useCvssCalculator = ({
  initialScore,
  initialVector,
  initialEnvironmentalVector,
  version: versionProp,
  environmentalRequirements,
  onScoreChange,
}: UseCvssCalculatorOptions) => {
  const appVersion: CvssVersion = versionProp ?? '3.1';
  const detectedVersion = initialVector
    ? getCvssVersionFromVector(toBaseMetricVector(initialVector))
    : null;
  const [sessionFindingVersion, setSessionFindingVersion] =
    useState<CvssVersion | null>(() => detectedVersion);
  const [forceAppVersion, setForceAppVersion] = useState(false);
  const [previousEnvironmentalVector] = useState(
    () => initialEnvironmentalVector ?? null,
  );
  const [previousFullVector] = useState(() => initialVector ?? null);
  const editingVersion: CvssVersion = forceAppVersion
    ? appVersion
    : (detectedVersion ?? sessionFindingVersion ?? appVersion);
  const shouldHydrateInitial = !forceAppVersion && detectedVersion != null;
  const initialBaseVector =
    shouldHydrateInitial && initialVector
      ? toBaseMetricVector(initialVector)
      : '';

  const [version, setVersion] = useState<CvssVersion>(editingVersion);
  const [metrics, setMetrics] = useState<CvssMetrics | Cvss4Metrics>(() =>
    initialBaseVector
      ? buildMetricsFromVector(initialBaseVector)
      : getEmptyMetrics(editingVersion),
  );
  const [calculatedScore, setCalculatedScore] = useState<string>(() =>
    initialBaseVector && initialScore ? initialScore : '',
  );
  const [vectorString, setVectorString] = useState<string>(
    () => initialBaseVector,
  );
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const onScoreChangeRef = useRef(onScoreChange);
  const lastEmittedRef = useRef<EmittedCvssScore | null>(null);
  const skipEmitRef = useRef(false);

  onScoreChangeRef.current = onScoreChange;

  const emitScoreChange = useCallback(
    (score: string, vector: string, severity: string) => {
      const lastEmitted = lastEmittedRef.current;

      if (
        lastEmitted &&
        lastEmitted.score === score &&
        lastEmitted.vector === vector &&
        lastEmitted.severity === severity
      ) {
        return;
      }

      lastEmittedRef.current = { score, vector, severity };
      onScoreChangeRef.current(score, vector, severity);
    },
    [],
  );

  useEffect(() => {
    if (detectedVersion) {
      setSessionFindingVersion(detectedVersion);
    }
  }, [detectedVersion]);

  useEffect(() => {
    if (detectedVersion === appVersion) {
      setForceAppVersion(false);
    }
  }, [detectedVersion, appVersion]);

  useEffect(() => {
    const incomingBaseVector = initialVector
      ? toBaseMetricVector(initialVector)
      : '';
    const lastEmitted = lastEmittedRef.current;
    const isEchoOfLastEmit =
      lastEmitted != null &&
      toBaseMetricVector(lastEmitted.vector) === incomingBaseVector &&
      lastEmitted.score === (initialScore ?? '');

    if (hasUserInteracted && isEchoOfLastEmit) {
      return;
    }

    if (hasUserInteracted) {
      setHasUserInteracted(false);
    }

    skipEmitRef.current = true;
    setVersion(editingVersion);

    if (shouldHydrateInitial && initialVector) {
      const baseVector = toBaseMetricVector(initialVector);

      setMetrics(buildMetricsFromVector(baseVector));
      setVectorString(baseVector);
      setCalculatedScore(initialScore ?? '');
      lastEmittedRef.current = {
        score: initialScore ?? '',
        vector: baseVector,
        severity: initialScore
          ? getSeverityFromScore(Number(initialScore))
          : 'Info',
      };

      return;
    }

    if (forceAppVersion) {
      return;
    }

    setMetrics(getEmptyMetrics(editingVersion));
    setVectorString('');
    setCalculatedScore('');
    lastEmittedRef.current = { score: '', vector: '', severity: 'Info' };
  }, [
    forceAppVersion,
    hasUserInteracted,
    initialScore,
    initialVector,
    shouldHydrateInitial,
    editingVersion,
  ]);

  useEffect(() => {
    const shouldEmit = hasUserInteracted && !skipEmitRef.current;

    if (areAllMetricsSelected(metrics as CvssMetrics | Cvss4Metrics)) {
      const score = calculateCvssScore(metrics as CvssMetrics | Cvss4Metrics);
      const vector = generateVectorString(
        metrics as CvssMetrics | Cvss4Metrics,
        version === '4.0' ? '4.0' : version === '3.0' ? '3.0' : '3.1',
      );
      const severity = getSeverityFromScore(score);
      const scoreString = score.toFixed(1);

      setCalculatedScore(scoreString);
      setVectorString(vector);

      if (shouldEmit) {
        emitScoreChange(scoreString, vector, severity);
      }
    } else {
      setCalculatedScore('');
      setVectorString('');
      if (shouldEmit) {
        emitScoreChange('', '', 'Info');
      }
    }

    skipEmitRef.current = false;
  }, [metrics, emitScoreChange, hasUserInteracted, version]);

  const handleMetricChange = useCallback(
    (metric: CvssMetricKey | Cvss4MetricKey, value: string) => {
      setHasUserInteracted(true);
      setMetrics((prev) => ({ ...prev, [metric]: value }));
    },
    [],
  );

  const resetCalculator = useCallback(() => {
    setHasUserInteracted(true);
    setVersion(editingVersion);
    setMetrics(getEmptyMetrics(editingVersion));
  }, [editingVersion]);

  const switchToAppVersion = useCallback(() => {
    setForceAppVersion(true);
    setSessionFindingVersion(null);
    setHasUserInteracted(true);
    setVersion(appVersion);
    setMetrics(getEmptyMetrics(appVersion));
    setVectorString('');
    setCalculatedScore('');
    emitScoreChange('', '', 'Info');
  }, [appVersion, emitScoreChange]);

  const requirementsForScoring = useMemo(
    () =>
      resolveEnvironmentalRequirementsForFindingVersion({
        requirements: environmentalRequirements,
        findingVersion: version,
        applicationVersion: appVersion,
        previousVector: previousFullVector,
        previousEnvironmentalVector,
      }),
    [
      appVersion,
      environmentalRequirements,
      previousEnvironmentalVector,
      previousFullVector,
      version,
    ],
  );
  const hasEnvironmentalRequirements = isEnvironmentalRequirementsConfigured(
    requirementsForScoring,
  );

  const environmentalDisplay = useMemo((): EnvironmentalDisplay | null => {
    if (!hasEnvironmentalRequirements || !vectorString) {
      return null;
    }

    const result = calculateEnvironmentalScore({
      version: version === '4.0' ? '4.0' : version === '3.0' ? '3.0' : '3.1',
      baseVector: vectorString,
      baseMetrics: metrics,
      requirements: requirementsForScoring,
    });

    if (!result?.isEnvironmentalConfigured) {
      return null;
    }

    const fullVector = result.environmentalVector
      ? `${vectorString}/${result.environmentalVector}`
      : vectorString;

    return {
      score: result.environmentalScore.toFixed(1),
      vector: fullVector,
    };
  }, [
    hasEnvironmentalRequirements,
    metrics,
    requirementsForScoring,
    vectorString,
    version,
  ]);

  return {
    version,
    appVersion,
    metrics,
    calculatedScore,
    vectorString,
    hasEnvironmentalRequirements,
    environmentalRequirementsForDisplay: requirementsForScoring,
    includeModifiedEnvironmentalMetrics: true,
    environmentalDisplay,
    handleMetricChange,
    resetCalculator,
    switchToAppVersion,
  };
};
